// src/lib/useKmClient.js
//
// scope='sa' (default): usato da SelfAssessmentPage.jsx. Percorso pubblico
// (Specifica v1.1 §4.1): nessun JWT, l'autenticazione passa dal token
// monouso nella query string, non da un header Authorization. Per questo
// getAuthToken restituisce sempre null qui — non è un bug, è il
// comportamento corretto per una rotta pubblica. Governato da
// KM_CONFIG.MOCK_MODE — passato a false il 22/09: /sa/message era rimasto
// "non verificato" quando questo default fu scelto, da allora verificato a
// fondo (due bug reali trovati e corretti in dto.js/useSelfAssessmentSession.js,
// intero flusso GDPR->domande testato end-to-end via terminale). Il motivo
// originale del default true non vale più.
//
// Override token demo- (23/09): il selettore di profilo nell'Hub
// (SA_DEMO_PROFILES) costruisce link tipo /self-assessment?token=demo-sa1,
// pensati per il client finto — ma era agganciato a MOCK_MODE, che ora è
// sempre false. Anziché riaccendere MOCK_MODE globalmente (romperebbe di
// nuovo il flusso vero), questo client controlla il token stesso: se
// inizia con "demo-", usa il client finto SOLO per quella sessione,
// indipendentemente dal flag globale. Un token vero (da /hr/token) non
// inizia mai con "demo-", quindi il flusso reale non è mai toccato da
// questo override.
//
// scope='km': usato da Esperienze/Ricerca/Schede. Rotte protette
// (Specifica v1.1 §4.2, richiedono is_hr/is_management/admin) — qui SERVE
// il vero bearer token di sessione TalentGPT, preso dal vero AuthContext
// (../context/AuthContext), non da lib/auth.jsx (quello è solo il
// simulatore di ruolo per le pagine KM, provvisorio, mai stato la fonte
// del token — usarlo come tale dava sempre token vuoto, quindi sempre 401
// "autenticazione richiesta" appena si esce dal mock). Governato da
// KM_CONFIG.KM_MOCK_MODE, separato da MOCK_MODE.

import { useMemo, useState } from 'react';
import { createKmClient } from '../api/client.js';
import { createMockKmClient } from '../api/mockClient.js';
import { KM_CONFIG } from '../api/config.js';
import { useAuth as useRealAuth } from '../context/AuthContext';

// Override token demo- (23/09, corretto lo stesso giorno dopo un bug
// trovato dal vivo): il selettore di profilo nell'Hub costruisce link tipo
// /self-assessment?token=demo-sa1. PRIMA VERSIONE, sbagliata: rileggeva
// window.location.search a ogni render — ma useSelfAssessmentSession.js
// CANCELLA il token dall'URL subito dopo /sa/open (history.replaceState,
// §4.3, sicurezza: non lasciare il token monouso visibile nella barra
// indirizzi). Risultato: al momento di /sa/message il client si ricreava
// da capo, non trovava più "demo-" nell'URL, e passava al client vero —
// che non conosce quella sessione finta e risponde 500. Ora la decisione
// è catturata UNA SOLA VOLTA al primo montaggio (useState con inizializzatore
// pigro, mai ricalcolato), non ricalcolata a ogni render.
function tokenDaUrlEDemo() {
  if (typeof window === 'undefined') return false;
  const token = new URLSearchParams(window.location.search).get('token');
  return typeof token === 'string' && token.startsWith('demo-');
}

export function useKmClient(scope = 'sa') {
  const { token: realToken, logout } = useRealAuth();
  // Lazy initializer: gira SOLO al primo render di questo componente, mai
  // più dopo — cattura la presenza del token demo- prima che venga
  // eventualmente ripulito dall'URL da altrove nel flusso.
  const [eraDemo] = useState(tokenDaUrlEDemo);
  const mock = scope === 'km'
    ? KM_CONFIG.KM_MOCK_MODE
    : (KM_CONFIG.MOCK_MODE || eraDemo);

  const getAuthToken = scope === 'km' ? () => realToken : () => null;
  const onUnauthorized = scope === 'km' ? logout : () => {};

  return useMemo(() => {
    if (mock) return createMockKmClient();
    return createKmClient({ getAuthToken, onUnauthorized });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mock, realToken, logout, scope]);
}
